<script lang="ts">
  import { Farmacia, type Medicamento, type Lote } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import { session } from '../lib/auth.svelte';

  let meds = $state<Medicamento[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);

  let nuevoMed = $state<Medicamento>({ codigo: '', nombre: '', presentacion: '', requiereReceta: true, esControlado: false });
  let nuevoLote = $state<Lote>({ medicamentoId: '', bodega: 'FAR-HOSP', numeroLote: '', vencimiento: '', cantidad: 0 });
  let disp = $state<{ medicamentoId: string; pacienteId: string; cantidad: number }>({ medicamentoId: '', pacienteId: '', cantidad: 1 });
  let stock = $state<Lote[]>([]);

  async function cargar() {
    loading = true; error = null;
    try { meds = await Farmacia.meds(); }
    catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function crearMed(e: SubmitEvent) {
    e.preventDefault();
    try { await Farmacia.crearMed({ ...nuevoMed }); nuevoMed = { codigo: '', nombre: '', presentacion: '', requiereReceta: true, esControlado: false }; await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function crearLote(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevoLote.medicamentoId) { error = 'Selecciona medicamento.'; return; }
    try {
      await Farmacia.crearLote({ ...nuevoLote, cantidad: Number(nuevoLote.cantidad) });
      nuevoLote = { ...nuevoLote, numeroLote: '', vencimiento: '', cantidad: 0 };
      await refrescarStock();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function refrescarStock() {
    stock = nuevoLote.medicamentoId ? await Farmacia.disponibles(nuevoLote.medicamentoId) : [];
  }

  async function dispensar(e: SubmitEvent) {
    e.preventDefault();
    error = null; info = null;
    if (!disp.medicamentoId || !disp.pacienteId) { error = 'Medicamento y paciente requeridos.'; return; }
    try {
      const r = await Farmacia.dispensar({
        medicamentoId: disp.medicamentoId,
        pacienteId: disp.pacienteId,
        cantidad: Number(disp.cantidad),
        dispensadoPor: session.value?.usuario ?? 'sistema'
      });
      info = `Dispensadas ${r.dispensado} unidades (FEFO).`;
      disp = { ...disp, cantidad: 1 };
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  $effect(() => { cargar(); });
  $effect(() => { if (nuevoLote.medicamentoId) refrescarStock(); });
</script>

<section>
  <header><h2>Farmacia hospitalaria</h2><button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button></header>
  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <h3>Medicamentos</h3>
  <form onsubmit={crearMed} class="row">
    <input placeholder="Código" bind:value={nuevoMed.codigo} required />
    <input placeholder="Nombre" bind:value={nuevoMed.nombre} required />
    <input placeholder="Presentación" bind:value={nuevoMed.presentacion} />
    <label class="chk"><input type="checkbox" bind:checked={nuevoMed.requiereReceta} /> Receta</label>
    <label class="chk"><input type="checkbox" bind:checked={nuevoMed.esControlado} /> Controlado</label>
    <button type="submit">+ Medicamento</button>
  </form>
  <table>
    <thead><tr><th>Código</th><th>Nombre</th><th>Presentación</th><th>Tipo</th></tr></thead>
    <tbody>
      {#each meds as m (m.id)}
        <tr>
          <td>{m.codigo}</td><td>{m.nombre}</td><td>{m.presentacion ?? '—'}</td>
          <td>
            {#if m.requiereReceta}<span class="tag">Rx</span>{/if}
            {#if m.esControlado}<span class="tag ctrl">Controlado</span>{/if}
          </td>
        </tr>
      {:else}<tr><td colspan="4" class="empty">No hay medicamentos en el cat�logo.</td></tr>{/each}
    </tbody>
  </table>

  <h3 style="margin-top:1.5rem">Lotes & stock</h3>
  <form onsubmit={crearLote} class="form-grid">
    <select bind:value={nuevoLote.medicamentoId} required>
      <option value="">— Medicamento —</option>
      {#each meds as m}<option value={m.id}>{m.codigo} · {m.nombre}</option>{/each}
    </select>
    <input placeholder="Bodega" bind:value={nuevoLote.bodega} />
    <input placeholder="N° Lote" bind:value={nuevoLote.numeroLote} required />
    <input type="date" bind:value={nuevoLote.vencimiento} required />
    <input type="number" min="1" placeholder="Cantidad" bind:value={nuevoLote.cantidad} required />
    <button type="submit" class="full">+ Lote</button>
  </form>

  {#if stock.length > 0}
    <table style="margin-top: .5rem">
      <thead><tr><th>Lote</th><th>Bodega</th><th>Vence</th><th class="num">Stock</th></tr></thead>
      <tbody>
        {#each stock as l (l.id)}
          <tr><td>{l.numeroLote}</td><td>{l.bodega ?? '—'}</td><td>{l.vencimiento}</td><td class="num">{l.cantidad}</td></tr>
        {/each}
      </tbody>
    </table>
  {/if}

  <h3 style="margin-top:1.5rem">Dispensar (FEFO)</h3>
  <form onsubmit={dispensar} class="form-grid">
    <select bind:value={disp.medicamentoId} required>
      <option value="">— Medicamento —</option>
      {#each meds as m}<option value={m.id}>{m.codigo} · {m.nombre}</option>{/each}
    </select>
    <PacientePicker bind:value={disp.pacienteId} onPick={(id) => disp.pacienteId = id} />
    <input type="number" min="1" bind:value={disp.cantidad} required />
    <button type="submit" class="full">Dispensar</button>
  </form>
</section>

<style>
  header { display: flex; justify-content: space-between; align-items: center; }
  h3 { margin: 1rem 0 .5rem; font-size: 1rem; color: #555; }
  .row { display: flex; gap: .5rem; flex-wrap: wrap; align-items: center; }
  .row input, .row button, .form-grid input, .form-grid select, .form-grid button, .form-grid > :global(.picker) {
    padding: .45rem .6rem; border-radius: 8px; border: 1px solid #ccc; font: inherit;
  }
  .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: .5rem; }
  .full, button[type=submit] { background: #0d6efd; color: #fff; border: 0; cursor: pointer; }
  .full { grid-column: span 3; }
  .chk { font-size: .85rem; display: flex; align-items: center; gap: .25rem; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: .45rem .6rem; border-bottom: 1px solid #eee; text-align: left; font-size: .9rem; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .tag { background: #eef; color: #335; padding: .1rem .45rem; border-radius: 6px; font-size: .72rem; margin-right: .25rem; }
  .tag.ctrl { background: #fde2e2; color: #a02020; }
  .empty { text-align: center; color: #888; font-style: italic; }
  .err { color: #b00020; }
  .ok { color: #1a6b1a; background: #d6f5d6; padding: .35rem .55rem; border-radius: 8px; }
</style>
